import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Spin,
  message,
  Row,
  Col,
  Typography,
  Modal,
  Space,
  Divider,
  Empty,
  Tooltip,
  Tag,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  ShopOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  GlobalOutlined,
  EditOutlined,
  SearchOutlined,
  SafetyOutlined,
  ExclamationCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useState, useMemo } from 'react';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import type { SocietyDetailResponse, DocumentResponse } from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { CopyIdButton } from '@/components/CopyIdButton';
import { ConfirmDeleteButton } from '@/components/ConfirmDeleteButton';
import { DocumentList } from '@/components/DocumentList';
import { StatusBadge, WisecureCard } from '@/components/base';
import { neutral, primary, success, error, warning } from '@/theme';

const { Text, Title } = Typography;

const SOCIETY_TYPES = [
  { value: 'SECURITY', label: 'Sécurité' },
  { value: 'INSTALLER', label: 'Installateur' },
  { value: 'DISTRIBUTOR', label: 'Distributeur' },
];

interface PaginatedDocuments {
  data: DocumentResponse[];
  total: number;
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  PENDING: { color: warning[400], icon: <ClockCircleOutlined />, label: 'En attente' },
  WAITING_ON_INFORMATION: { color: warning[300], icon: <InfoCircleOutlined />, label: 'En attente d\'informations' },
  VALIDATED: { color: success[400], icon: <CheckCircleOutlined />, label: 'Validé' },
  REJECTED: { color: error[400], icon: <CloseCircleOutlined />, label: 'Refusé' },
};
const fallbackStatus = { color: warning[400], icon: <ClockCircleOutlined />, label: 'En attente' };

export function SocietyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidateQueries();
  const [form] = Form.useForm();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoReason, setInfoReason] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { data: society, isLoading } = useWisecureQuery<SocietyDetailResponse>(
    queryKeys.societyDetail(id!),
    (client) => client.societies.societiesControllerFindOne({ id: id! }),
    { enabled: !!id },
  );

  const { data: docsData, isLoading: docsLoading } = useWisecureQuery<PaginatedDocuments>(
    queryKeys.documents('society', id!),
    (client) =>
      client.documents.documentsControllerFindAll({
        entityType: 'society',
        entityId: id,
      }) as Promise<PaginatedDocuments>,
    { enabled: !!id },
  );

  type DocStatus = {
    societyId?: string;
    societyStatus?: string;
    canUseApp?: boolean;
    hasPendingDocuments?: boolean;
    hasRejectedDocuments?: boolean;
    documents?: Array<{
      type?: string;
      status?: string;
      statusDescription?: string;
    }>;
    agentDocumentStatus?: Array<{
      agentId?: string;
      firstName?: string;
      lastName?: string;
      status?: string;
      proCardStatus?: string;
      idCardStatus?: string;
    }>;
  };

  const { data: docStatus } = useWisecureQuery<DocStatus>(
    queryKeys.societyDocumentStatus(id!),
    (client) => client.societies.societiesControllerGetDocumentStatus({ id: id! }),
    { enabled: !!id },
  );

  const updateMutation = useWisecureMutation(
    (client, values: Record<string, unknown>) =>
      client.societies.societiesControllerUpdate({ id: id!, requestBody: values }),
    {
      onSuccess: () => {
        message.success('Société sauvegardée');
        setIsEditing(false);
        invalidate(queryKeys.societyDetail(id!));
      },
      onError: () => message.error('Erreur lors de la sauvegarde de la société'),
    },
  );

  const validateMutation = useWisecureMutation(
    (client, { status, reason }: { status: string; reason?: string }) =>
      client.societies.societiesControllerValidate({
        id: id!,
        requestBody: { status: status as 'VALIDATED' | 'REJECTED' | 'WAITING_ON_INFORMATION', reason },
      }),
    {
      onSuccess: () => {
        message.success('Statut mis à jour');
        invalidate(queryKeys.societyDetail(id!));
      },
      onError: () => message.error('Erreur lors de la mise à jour du statut'),
    },
  );

  const deleteMutation = useWisecureMutation(
    (client) => client.societies.societiesControllerRemove({ id: id! }),
    {
      onSuccess: () => {
        message.success('Société supprimée');
        navigate('/societies/validation');
      },
      onError: () => message.error('Erreur lors de la suppression de la société'),
    },
  );

  const verifySiretMutation = useWisecureMutation(
    (client, siret: string) =>
      client.siretVerification.siretVerificationControllerVerify({ requestBody: { siret } }),
    {
      onSuccess: (data) => {
        if (data.status === 'visible') {
          form.setFieldsValue({
            number: data.streetNumber ?? '',
            street: data.streetName ?? '',
            city: data.city ?? '',
            zip: data.zipCode ?? '',
          });
          message.success('Adresse auto-remplie depuis le SIRET');
        } else if (data.status === 'hidden') {
          message.warning('Le SIRET existe mais les données sont restreintes');
        } else {
          message.error('Numéro SIRET invalide');
        }
      },
      onError: () => message.error('Erreur lors de la vérification du SIRET'),
    },
  );

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload: Record<string, unknown> = {};
      if (values.name !== society?.name) payload.name = values.name;
      if (values.societyIdentifier !== society?.societyIdentifier)
        payload.societyIdentifier = values.societyIdentifier;
      if (values.type !== society?.type) payload.type = values.type;
      if (values.email !== society?.email) payload.email = values.email;
      if (values.phoneCountryCode || values.phoneNumber) {
        payload.phone = {
          countryCode: values.phoneCountryCode || 'FR',
          number: values.phoneNumber,
        };
      }
      if (values.description !== society?.description) payload.description = values.description;
      if (values.number || values.street || values.zip || values.city || values.country) {
        payload.headQuarterAddress = {
          number: values.number,
          street: values.street,
          zip: values.zip,
          city: values.city,
          country: values.country,
        };
      }
      if (Object.keys(payload).length === 0) {
        message.info('Aucune modification détectée');
        return;
      }
      updateMutation.mutate(payload);
    });
  };

  const handleRejectConfirm = () => {
    validateMutation.mutate({ status: 'REJECTED', reason: rejectReason });
    setRejectModalOpen(false);
    setRejectReason('');
  };

  const handleInfoConfirm = () => {
    validateMutation.mutate({ status: 'WAITING_ON_INFORMATION', reason: infoReason });
    setInfoModalOpen(false);
    setInfoReason('');
  };

  const allDocs = useMemo(() => docsData?.data ?? [], [docsData]);
  const pendingDocs = useMemo(() => allDocs.filter((d) => d.status === 'PENDING'), [allDocs]);
  const hasPendingDocs = pendingDocs.length > 0;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!society) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Empty description="Société non trouvée">
          <Button onClick={() => navigate('/societies/validation')}>Retour à la liste</Button>
        </Empty>
      </div>
    );
  }

  const hq = society.headQuarterAddress;
  const status = statusConfig[society.status] ?? fallbackStatus;
  const docsCount = docsData?.total ?? 0;
  const agentsCount = society.agents?.length ?? 0;
  const sectorsCount = society.sectors?.length ?? 0;

  return (
    <div style={{ maxWidth: 1200 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ color: neutral[500] }}
        />
        <Text style={{ color: neutral[300], fontSize: 13 }}>Sociétés</Text>
        <Text style={{ color: neutral[200] }}>/</Text>
        <Text style={{ color: neutral[700], fontWeight: 500, fontSize: 13 }}>{society.name}</Text>
      </div>

      {docStatus?.canUseApp === false && (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message="Société bloquée"
          description="Cette société ne peut pas utiliser l'application en raison de documents refusés ou manquants. Les agents ne peuvent pas intervenir."
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      {docStatus?.hasRejectedDocuments && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Documents refusés"
          description="Un ou plusieurs documents ont été refusés. La société doit resoumettre les documents nécessaires."
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      {docStatus?.hasPendingDocuments && !docStatus?.hasRejectedDocuments && (
        <Alert
          type="warning"
          showIcon
          icon={<ClockCircleOutlined />}
          message="Documents en attente"
          description="Des documents sont en attente de validation. Examinez-les avant de valider la société."
          style={{ marginBottom: 16, borderRadius: 10 }}
        />
      )}

      <WisecureCard
        dsVariant="highlight"
        style={{ marginBottom: 24, padding: 0 }}
        styles={{ body: { padding: '28px 32px' } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: primary[600],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShopOutlined style={{ fontSize: 24, color: neutral[0] }} />
            </div>
            <div>
              <Title level={3} style={{ margin: 0, color: neutral[0], fontWeight: 700, letterSpacing: '-0.5px' }}>
                {society.name}
              </Title>
              <Space size={8} style={{ marginTop: 6 }} wrap>
                <StatusBadge status={society.type} />
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: `${status.color}20`,
                    color: status.color,
                    borderRadius: 100,
                    padding: '3px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {status.icon}
                  {status.label}
                </div>
              </Space>
            </div>
          </div>

          <Space size={8}>
            <CopyIdButton
              id={society.id}
              type="primary"
              ghost
              size="small"
            />
            <ConfirmDeleteButton
              onConfirm={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
              type="primary"
              ghost
              danger
              size="small"
            >
              Supprimer
            </ConfirmDeleteButton>
          </Space>
        </div>

        <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0 16px' }} />

        <Row gutter={24}>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Agents</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>{agentsCount}</Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Secteurs</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>{sectorsCount}</Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Documents</Text>
              <Text style={{ color: neutral[0], fontSize: 22, fontWeight: 700 }}>{docsCount}</Text>
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div style={{ textAlign: 'center' }}>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 4 }}>Créé le</Text>
              <Text style={{ color: neutral[0], fontSize: 14, fontWeight: 500 }}>
                {new Date(society.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Text>
            </div>
          </Col>
        </Row>

        {docStatus?.documents && docStatus.documents.length > 0 && (
          <>
            <Divider style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />
            <div>
              <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, display: 'block', marginBottom: 8 }}>
                Statut des documents
              </Text>
              <Space size={8} wrap>
                {docStatus.documents.map((doc) => {
                  const docStatusLabel: Record<string, { color: string; label: string }> = {
                    VALIDATED: { color: success[400], label: 'Validé' },
                    PENDING: { color: warning[400], label: 'En attente' },
                    DENIED: { color: error[400], label: 'Refusé' },
                    NOT_UPLOADED: { color: neutral[300], label: 'Non soumis' },
                  };
                  const ds = docStatusLabel[doc.status ?? ''] ?? { color: neutral[300], label: doc.status ?? '—' };
                  const typeLabel: Record<string, string> = {
                    KBIS: 'KBIS',
                    COMPANY_AUTHORIZATION: 'Autorisation',
                    CNAPS: 'CNAPS',
                  };
                  return (
                    <div
                      key={doc.type}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: `${ds.color}18`,
                        color: ds.color,
                        borderRadius: 100,
                        padding: '4px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {typeLabel[doc.type ?? ''] ?? doc.type}: {ds.label}
                    </div>
                  );
                })}
              </Space>
            </div>
          </>
        )}
      </WisecureCard>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          <WisecureCard
            style={{ marginBottom: 24 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Informations</span>
                {!isEditing && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => setIsEditing(true)}
                    size="small"
                    style={{ color: primary[400] }}
                  >
                    Modifier
                  </Button>
                )}
              </div>
            }
          >
            {!isEditing ? (
              <div>
                <Row gutter={[16, 20]}>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Email</Text>
                      <Space size={8}>
                        <MailOutlined style={{ color: neutral[300], fontSize: 14 }} />
                        <Text style={{ fontWeight: 500 }}>{society.email ?? '—'}</Text>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Téléphone</Text>
                      <Space size={8}>
                        <PhoneOutlined style={{ color: neutral[300], fontSize: 14 }} />
                        <Text style={{ fontWeight: 500 }}>{society.phone?.fullNumber ? String(society.phone.fullNumber) : '—'}</Text>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Identifiant</Text>
                      <Text style={{ fontWeight: 500 }}>{society.societyIdentifier ?? '—'}</Text>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Type</Text>
                      <StatusBadge status={society.type} />
                    </div>
                  </Col>
                  {society.ownerFirstName && (
                    <Col xs={24} sm={12}>
                      <div>
                        <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Propriétaire</Text>
                        <Space size={8}>
                          <SafetyOutlined style={{ color: neutral[300], fontSize: 14 }} />
                          <Text style={{ fontWeight: 500 }}>
                            {society.ownerFirstName} {society.ownerLastName}
                          </Text>
                        </Space>
                      </div>
                    </Col>
                  )}
                  {society.sponsorshipCode && (
                    <Col xs={24} sm={12}>
                      <div>
                        <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 4 }}>Code parrainage</Text>
                        <Text style={{ fontWeight: 500, fontFamily: 'monospace' }}>{society.sponsorshipCode}</Text>
                      </div>
                    </Col>
                  )}
                </Row>

                {hq && (hq.number || hq.street || hq.city) && (
                  <>
                    <Divider style={{ margin: '20px 0' }} />
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 8 }}>
                        <EnvironmentOutlined style={{ marginRight: 6 }} />
                        Siège social
                      </Text>
                      <Text style={{ fontWeight: 500 }}>
                        {[hq.number, hq.street].filter(Boolean).join(' ')}
                        {[hq.zip, hq.city].filter(Boolean).join(' ')}
                        {hq.country ? ` (${hq.country})` : ''}
                      </Text>
                    </div>
                  </>
                )}

                {society.description && (
                  <>
                    <Divider style={{ margin: '20px 0' }} />
                    <div>
                      <Text style={{ color: neutral[300], fontSize: 12, display: 'block', marginBottom: 8 }}>Description</Text>
                      <Text>{society.description}</Text>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                initialValues={{
                  name: society.name,
                  societyIdentifier: society.societyIdentifier ?? '',
                  type: society.type,
                  email: society.email ?? '',
                  phoneCountryCode: society.phone?.countryCode ?? '+33',
                  phoneNumber: society.phone?.number ?? '',
                  description: society.description ?? '',
                  number: hq?.number ?? '',
                  street: hq?.street ?? '',
                  zip: hq?.zip ?? '',
                  city: hq?.city ?? '',
                  country: hq?.country ?? '',
                }}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="name" label="Nom" rules={[{ required: true, message: 'Champ requis' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="societyIdentifier"
                      label="Identifiant société"
                      rules={[{ required: true, message: 'Champ requis' }]}
                    >
                      <Input
                        addonAfter={
                          <Button
                            type="link"
                            icon={<SearchOutlined />}
                            onClick={() => {
                              const siret = form.getFieldValue('societyIdentifier');
                              if (siret?.length === 14 && /^\d{14}$/.test(siret)) {
                                verifySiretMutation.mutate(siret);
                              } else {
                                message.warning('Le SIRET doit contenir 14 chiffres');
                              }
                            }}
                            loading={verifySiretMutation.isPending}
                            style={{ padding: 0 }}
                          >
                            Vérifier
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="type" label="Type">
                      <Select options={SOCIETY_TYPES} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Adresse email invalide' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={6}>
                    <Form.Item name="phoneCountryCode" label="Indicatif">
                      <Input placeholder="+33" />
                    </Form.Item>
                  </Col>
                  <Col xs={16} sm={6}>
                    <Form.Item name="phoneNumber" label="Téléphone">
                      <Input placeholder="612345678" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item name="description" label="Description">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '8px 0 16px' }} />
                <Text style={{ color: neutral[500], fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 12 }}>
                  Siège social
                </Text>
                <Row gutter={16}>
                  <Col xs={8} sm={6}>
                    <Form.Item
                      name="number"
                      label="Numéro"
                      rules={[{ pattern: /^[0-9]+[a-zA-Z\s]*$/, message: 'Format invalide' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={16} sm={18}>
                    <Form.Item name="street" label="Rue">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={8} sm={8}>
                    <Form.Item
                      name="zip"
                      label="Code postal"
                      rules={[{ pattern: /^[0-9]+$/, message: 'Chiffres uniquement' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={16} sm={10}>
                    <Form.Item name="city" label="Ville">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      name="country"
                      label="Pays"
                      rules={[{ pattern: /^[a-zA-Z]{2}$/, message: '2 lettres' }]}
                    >
                      <Input maxLength={2} placeholder="FR" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '8px 0 16px' }} />
                <Space>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    loading={updateMutation.isPending}
                    disabled={updateMutation.isPending}
                  >
                    Sauvegarder
                  </Button>
                  <Button
                    disabled={updateMutation.isPending}
                    onClick={() => {
                      setIsEditing(false);
                      form.resetFields();
                    }}
                  >
                    Annuler
                  </Button>
                </Space>
              </Form>
            )}
          </WisecureCard>

          {society.sectors && society.sectors.length > 0 && (
            <WisecureCard
              title={
                <Space>
                  <GlobalOutlined style={{ color: primary[400] }} />
                  <span>Secteurs</span>
                  <Text style={{ color: neutral[300], fontSize: 13, fontWeight: 400 }}>({sectorsCount})</Text>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {society.sectors.map((sector) => (
                  <div
                    key={sector.id}
                    style={{
                      background: neutral[50],
                      border: `1px solid ${neutral[100]}`,
                      borderRadius: 10,
                      padding: '10px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      minWidth: 180,
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: primary[400],
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <Text style={{ fontWeight: 600, fontSize: 13, display: 'block' }}>{sector.label}</Text>
                      {sector.rangeKm && (
                        <Text style={{ color: neutral[300], fontSize: 11 }}>{sector.rangeKm} km</Text>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </WisecureCard>
          )}

          {society.agents && society.agents.length > 0 && (
            <WisecureCard
              title={
                <Space>
                  <TeamOutlined style={{ color: primary[400] }} />
                  <span>Agents</span>
                  <Text style={{ color: neutral[300], fontSize: 13, fontWeight: 400 }}>({agentsCount})</Text>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {society.agents.map((agent) => (
                  <div
                    key={agent.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      background: neutral[50],
                      borderRadius: 10,
                      border: `1px solid ${neutral[100]}`,
                    }}
                  >
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: primary[100],
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <SafetyOutlined style={{ color: primary[400], fontSize: 16 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontWeight: 600, fontSize: 13, display: 'block' }}>
                        {agent.firstName} {agent.lastName}
                      </Text>
                      {agent.currentSectorLabel && (
                        <Text style={{ color: neutral[300], fontSize: 12 }}>
                          {agent.currentSectorLabel}
                        </Text>
                      )}
                    </div>
                    <StatusBadge status={agent.status} />
                  </div>
                ))}
              </div>
            </WisecureCard>
          )}
        </Col>

        <Col xs={24} lg={10}>
          <WisecureCard
            title={
              <Space>
                <CheckCircleOutlined style={{ color: primary[400] }} />
                <span>Vérification</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <div
              style={{
                background: `${status.color}08`,
                border: `1px solid ${status.color}30`,
                borderRadius: 12,
                padding: '16px 20px',
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: `${status.color}18`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  color: status.color,
                }}
              >
                {status.icon}
              </div>
              <div>
                <Text style={{ fontSize: 12, color: neutral[300], display: 'block' }}>Statut actuel</Text>
                <Text style={{ fontWeight: 700, fontSize: 16, color: status.color }}>{status.label}</Text>
              </div>
            </div>

            <Text style={{ color: neutral[500], fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 10 }}>
              Changer le statut
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <Tooltip
                title={hasPendingDocs ? `${pendingDocs.length} document(s) en attente d'examen` : ''}
              >
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  block
                  onClick={() => validateMutation.mutate({ status: 'VALIDATED' })}
                  loading={validateMutation.isPending}
                  disabled={society.status === 'VALIDATED' || hasPendingDocs}
                  style={{
                    height: 44,
                    fontWeight: 600,
                    background: success[400],
                    borderColor: success[400],
                  }}
                >
                  Valider la société
                </Button>
              </Tooltip>
              {hasPendingDocs && (
                <div
                  style={{
                    background: `${warning[400]}08`,
                    border: `1px solid ${warning[400]}20`,
                    borderRadius: 10,
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <ExclamationCircleOutlined style={{ color: warning[400], fontSize: 14, flexShrink: 0 }} />
                  <Text style={{ color: neutral[500], fontSize: 12 }}>
                    {pendingDocs.length} document(s) en attente — examinez-les avant de valider
                  </Text>
                </div>
              )}
              <Button
                danger
                icon={<CloseCircleOutlined />}
                block
                onClick={() => setRejectModalOpen(true)}
                disabled={society.status === 'REJECTED'}
                style={{ height: 44, fontWeight: 600 }}
              >
                Refuser la société
              </Button>
              <Button
                block
                icon={<InfoCircleOutlined />}
                onClick={() => setInfoModalOpen(true)}
                disabled={society.status === 'WAITING_ON_INFORMATION'}
                style={{ height: 44, fontWeight: 600 }}
              >
                Demander des informations
              </Button>
            </Space>
          </WisecureCard>

          <WisecureCard
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Documents</span>
                {docsCount > 0 && (
                  <Text style={{ color: neutral[300], fontSize: 13, fontWeight: 400 }}>{docsCount}</Text>
                )}
              </div>
            }
          >
            <DocumentList
              documents={((docsData?.data ?? []) as unknown as import('@/components/DocumentList').DocumentItem[])}
              isLoading={docsLoading}
              entityType="society"
              entityId={id!}
            />
          </WisecureCard>

          {society.stripeAccount && (
            <WisecureCard
              title={
                <Space>
                  <DollarOutlined style={{ color: '#635bff' }} />
                  <span>Stripe Connect</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: neutral[300], fontSize: 12 }}>Stripe ID</Text>
                  <Text copyable style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
                    {society.stripeAccount.stripeId}
                  </Text>
                </div>
                {society.stripeAccount.currency && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: neutral[300], fontSize: 12 }}>Devise</Text>
                    <Tag>{society.stripeAccount.currency.toUpperCase()}</Tag>
                  </div>
                )}
                {society.stripeAccount.pendingBalance != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: neutral[300], fontSize: 12 }}>Solde en attente</Text>
                    <Text style={{ fontWeight: 600 }}>
                      {(society.stripeAccount.pendingBalance / 100).toFixed(2)} {society.stripeAccount.currency?.toUpperCase() ?? ''}
                    </Text>
                  </div>
                )}
                {society.stripeAccount.availableBalance != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: neutral[300], fontSize: 12 }}>Solde disponible</Text>
                    <Text style={{ fontWeight: 600 }}>
                      {(society.stripeAccount.availableBalance / 100).toFixed(2)} {society.stripeAccount.currency?.toUpperCase() ?? ''}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: neutral[300], fontSize: 12 }}>Paiements acceptés</Text>
                  {society.stripeAccount.chargesEnabled ? (
                    <Space size={4}>
                      <CheckCircleOutlined style={{ color: success[400] }} />
                      <Text style={{ fontWeight: 500 }}>Oui</Text>
                    </Space>
                  ) : (
                    <Space size={4}>
                      <CloseCircleOutlined style={{ color: error[400] }} />
                      <Text style={{ fontWeight: 500 }}>Non</Text>
                    </Space>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text style={{ color: neutral[300], fontSize: 12 }}>Retraits activés</Text>
                  {society.stripeAccount.payoutsEnabled ? (
                    <Space size={4}>
                      <CheckCircleOutlined style={{ color: success[400] }} />
                      <Text style={{ fontWeight: 500 }}>Oui</Text>
                    </Space>
                  ) : (
                    <Space size={4}>
                      <CloseCircleOutlined style={{ color: error[400] }} />
                      <Text style={{ fontWeight: 500 }}>Non</Text>
                    </Space>
                  )}
                </div>
                {society.stripeAccount.detailsSubmitted != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: neutral[300], fontSize: 12 }}>Détails soumis</Text>
                    <Text style={{ fontWeight: 500 }}>
                      {society.stripeAccount.detailsSubmitted ? 'Oui' : 'Non'}
                    </Text>
                  </div>
                )}
                {society.stripeAccount.isActive != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ color: neutral[300], fontSize: 12 }}>Statut</Text>
                    <Tag color={society.stripeAccount.isActive ? 'success' : 'error'}>
                      {society.stripeAccount.isActive ? 'Actif' : 'Inactif'}
                    </Tag>
                  </div>
                )}
              </div>
            </WisecureCard>
          )}
        </Col>
      </Row>

      <Modal
        title="Refuser la société"
        open={rejectModalOpen}
        onOk={handleRejectConfirm}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectReason('');
        }}
        okText="Refuser"
        cancelText="Annuler"
        okButtonProps={{ danger: true, disabled: !rejectReason.trim() }}
      >
        <Input.TextArea
          rows={3}
          placeholder="Raison du refus (obligatoire)..."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />
      </Modal>

      <Modal
        title="Demander des informations"
        open={infoModalOpen}
        onOk={handleInfoConfirm}
        onCancel={() => {
          setInfoModalOpen(false);
          setInfoReason('');
        }}
        okText="Envoyer"
        cancelText="Annuler"
        okButtonProps={{ disabled: !infoReason.trim() }}
      >
        <Input.TextArea
          rows={3}
          placeholder="Précisez les informations manquantes (obligatoire)..."
          value={infoReason}
          onChange={(e) => setInfoReason(e.target.value)}
        />
      </Modal>
    </div>
  );
}
